import { useCallback, useRef, useState, type KeyboardEvent } from 'react'

/** How long typed characters accumulate into one type-ahead search. */
const TYPEAHEAD_MS = 500

export interface ListboxItem {
  label: string
  disabled?: boolean
}

export interface UseListboxOptions<T extends ListboxItem> {
  items: readonly T[]
  open: boolean
  setOpen: (open: boolean) => void
  /** Index of the selected item (where the highlight starts when the list opens), or -1. */
  selectedIndex: number
  onChoose: (item: T, index: number) => void
  /** Type-ahead on printable keys (off when a search box takes the typing). */
  typeAhead?: boolean
  /** Home/End move the highlight (off while a search box owns the caret). */
  homeEnd?: boolean
}

/**
 * Keyboard support for a listbox popup driven from a trigger with `aria-activedescendant` (Frontend §2.4):
 * arrows, Home/End, type-ahead, Enter/Space to choose, Escape to close. Focus stays on the trigger (or search box).
 */
export function useListbox<T extends ListboxItem>({
  items,
  open,
  setOpen,
  selectedIndex,
  onChoose,
  typeAhead = true,
  homeEnd = true,
}: UseListboxOptions<T>) {
  const [active, setActive] = useState(-1)
  const typed = useRef({ text: '', at: 0 })

  const enabled = useCallback((i: number) => i >= 0 && i < items.length && !items[i]?.disabled, [items])

  const step = useCallback(
    (from: number, dir: 1 | -1) => {
      for (let i = from + dir; i >= 0 && i < items.length; i += dir) if (enabled(i)) return i
      return from
    },
    [items.length, enabled],
  )
  const first = useCallback(() => step(-1, 1), [step])
  const last = useCallback(() => step(items.length, -1), [step, items.length])

  const openAt = useCallback(
    (index: number) => {
      setOpen(true)
      setActive(index)
    },
    [setOpen],
  )

  const findByPrefix = useCallback(
    (prefix: string, from: number) => {
      const lower = prefix.toLowerCase()
      for (let n = 1; n <= items.length; n++) {
        const i = (from + n) % items.length
        if (enabled(i) && items[i]?.label.toLowerCase().startsWith(lower)) return i
      }
      return -1
    },
    [items, enabled],
  )

  const close = () => {
    typed.current = { text: '', at: 0 }
    setOpen(false)
  }

  const onKeyDown = (e: KeyboardEvent) => {
    const start = enabled(selectedIndex) ? selectedIndex : first()
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        openAt(start)
      } else if (homeEnd && (e.key === 'Home' || e.key === 'End')) {
        e.preventDefault()
        openAt(e.key === 'Home' ? first() : last())
      } else if (typeAhead && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const hit = searchTyped(e.key, start - 1, e.timeStamp)
        openAt(hit === -1 ? start : hit)
      }
      return
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActive((a) => (a === -1 ? first() : step(a, 1)))
        return
      case 'ArrowUp':
        e.preventDefault()
        setActive((a) => (a === -1 ? last() : step(a, -1)))
        return
      case 'Home':
      case 'End':
        if (!homeEnd) return
        e.preventDefault()
        setActive(e.key === 'Home' ? first() : last())
        return
      case 'Enter':
      case ' ':
        if (e.key === ' ' && !typeAhead) return // a space typed into the search box
        if (e.key === ' ' && typed.current.text && e.timeStamp - typed.current.at < TYPEAHEAD_MS) {
          // A space inside a type-ahead word ("Dr. S…") keeps searching.
          searchAndSet(e.key, e.timeStamp)
          e.preventDefault()
          return
        }
        e.preventDefault()
        if (enabled(active)) {
          const item = items[active]
          if (item) onChoose(item, active)
        }
        close()
        return
      case 'Escape':
        e.preventDefault()
        // Close only this popup, not the Modal or Drawer around it.
        e.stopPropagation()
        close()
        return
      case 'Tab':
        close()
        return
      default:
        if (typeAhead && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          e.preventDefault()
          searchAndSet(e.key, e.timeStamp)
        }
    }
  }

  function searchTyped(key: string, from: number, now: number): number {
    const fresh = now - typed.current.at > TYPEAHEAD_MS
    typed.current = { text: fresh ? key : typed.current.text + key, at: now }
    const text = typed.current.text
    // Repeating one letter cycles through the items that start with it.
    const repeated = /^(.)\1+$/su.test(text)
    return findByPrefix(repeated ? key : text, repeated || text.length === 1 ? from : from - 1)
  }

  function searchAndSet(key: string, now: number) {
    const hit = searchTyped(key, active, now)
    if (hit !== -1) setActive(hit)
  }

  return { active, setActive, onKeyDown }
}
